import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { Cloud, ArrowUpRight, Check, Server, Shield, Globe, Zap, Clock, Lock, Users, HardDrive } from 'lucide-react';

const features = [
  { icon: Server, title: 'Global edge', text: 'Conversations are processed close to your customers for low latency.' },
  { icon: Shield, title: 'Compliance ready', text: 'GDPR and SOC 2 Type II in progress, with encryption at rest and in transit.' },
  { icon: Globe, title: 'Multi-region', text: 'Data stays in your region of choice with automatic failover.' },
  { icon: Zap, title: 'Auto-scale', text: 'Zuri Cloud scales from one agent to thousands without manual tuning.' },
  { icon: Lock, title: 'End-to-end encryption', text: 'Customer data is encrypted from the widget to the database.' },
  { icon: HardDrive, title: 'Backups', text: 'Automated daily backups with point-in-time recovery on Enterprise.' },
];

const useCases = [
  { title: 'Startups', text: 'Launch fast with a free plan and grow into Enterprise without platform changes.' },
  { title: 'Enterprises', text: 'Private cloud, SSO, and custom SLAs for regulated industries.' },
  { title: 'Agencies', text: 'Host multiple workspaces under one account with isolated data.' },
];

const comparison = [
  { feature: 'Built-in uptime SLA', cloud: true, aws: 'add-on', other: false },
  { feature: 'GDPR-ready by default', cloud: true, aws: 'add-on', other: true },
  { feature: 'Agent pricing included', cloud: true, aws: false, other: 'partial' },
  { feature: 'Managed support layer', cloud: true, aws: 'add-on', other: false },
];

export default function ZuriCloud() {
  return (
    <>
      <SEO
        title="Zuri Cloud"
        description="Zuri Cloud powers every conversation with global edge delivery, compliance, encryption, and 99.99% uptime."
        path="/zuri-cloud"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Cloud className="h-4 w-4" /> Infrastructure
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            The cloud built for <span className="gradient-text">CX at scale</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Zuri Cloud delivers the global, secure, and compliant backbone that hosts every conversation, file, and metric.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Try Zuri Cloud <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Built for reliability</h2>
            <p className="section-subtitle mx-auto mt-4">Global edge, encryption, and compliance out of the box.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="section-title">How it works</h2>
              <p className="mt-4 text-lg text-slate-600">
                Every message, file, and event routes through our edge network, is validated, encrypted, and stored in your chosen region. Zuri Cloud abstracts the complexity so your team can focus on customers.
              </p>
              <ol className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">1</span>
                  <span className="text-slate-700">Messages hit the nearest edge for validation and encryption.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">2</span>
                  <span className="text-slate-700">Data is written to durable storage with automatic backups.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">3</span>
                  <span className="text-slate-700">Agents and AI read from the same real-time source of truth.</span>
                </li>
              </ol>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 p-4">
                  <Globe className="h-5 w-5 text-brand-400" />
                  <span className="text-sm font-semibold">Edge validation</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 p-4">
                  <Lock className="h-5 w-5 text-brand-400" />
                  <span className="text-sm font-semibold">Encrypted at rest and in transit</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 p-4">
                  <Server className="h-5 w-5 text-brand-400" />
                  <span className="text-sm font-semibold">Regional storage with backups</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 id="compare-heading" className="mb-10 text-center section-title">How we compare</h2>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left font-bold text-slate-700">Feature</th>
                  <th className="px-6 py-4 text-center font-bold text-brand-700">Zuri Cloud</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-700">Raw Cloud</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-700">Others</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature} className="border-t border-slate-100">
                    <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                    <td className="px-6 py-4 text-center"><Check className="mx-auto h-5 w-5 text-green-600" /></td>
                    <td className="px-6 py-4 text-center">{row.aws === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.aws}</span>}</td>
                    <td className="px-6 py-4 text-center">{row.other === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.other}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="sla-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="sla-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Reliability & team</h2>
              <p className="mt-4 text-slate-300">
                A standard backend engineering team monitors Zuri Cloud around the clock. Enterprise customers get dedicated support and private cloud options.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-800/50 p-5">
                  <Users className="h-6 w-6 text-brand-400" />
                  <p className="mt-2 font-bold">Engineering team</p>
                  <p className="text-sm text-slate-400">SRE, security, and platform engineers on call</p>
                </div>
                <div className="rounded-2xl bg-slate-800/50 p-5">
                  <Clock className="h-6 w-6 text-brand-400" />
                  <p className="mt-2 font-bold">Uptime SLA</p>
                  <p className="text-sm text-slate-400">99.99% on Enterprise</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">SLA highlights</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> 99.99% uptime on Enterprise plans</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Median API response under 200ms</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Encrypted backups every 24 hours</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Incident response within 15 minutes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
